import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2, ScanText } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { excerptAround, findMatch } from "@/lib/textMatch";

const cache = new Map();
async function loadPage(caseId, docId, page, fresh = false) {
  const key = `${docId}:${page}`;
  if (!fresh && cache.has(key)) return cache.get(key);
  const r = await api.get(`/projects/${caseId}/documents/${docId}/pages`, { params: { page } });
  const out = { ocr: r.data.ocr, page: r.data.pages?.[0] || null };
  if (out.page?.text?.trim()) cache.set(key, out);
  return out;
}

/**
 * The passage in the customer's document the item came from – shown beside the item, with the matching text marked,
 * so checking a requirement rarely means opening the 114-page PDF. Scanned pages are read with OCR in the background.
 */
export function SourceExcerpt({ caseId, item, onOpen }) {
  const src = item?.source || {};
  const queries = useMemo(() => [item?.customer_text, item?.title].filter(Boolean), [item?.customer_text, item?.title]);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, page: null, ocr: null, err: null });
  const canShow = Boolean(src.doc_id && src.page);
  useEffect(() => {
    if (!open || !canShow) return undefined;
    let alive = true;
    let timer = null;
    const tick = async (fresh) => {
      setState((s) => ({ ...s, loading: !s.page }));
      try {
        const r = await loadPage(caseId, src.doc_id, src.page, fresh);
        if (!alive) return;
        setState({ loading: false, page: r.page, ocr: r.ocr, err: null });
        if (r.ocr && ["pending", "running"].includes(r.ocr.status)) timer = setTimeout(() => tick(true), 2500);
      } catch (e) { if (alive) setState({ loading: false, page: null, ocr: null, err: errMsg(e) }); }
    };
    tick(false);
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [open, canShow, caseId, src.doc_id, src.page]);
  if (!canShow) return null;
  const text = state.page?.text || "";
  const m = text ? findMatch(text, queries) : null;
  const ex = text ? excerptAround(text, m) : null;
  const ocrBusy = state.ocr && ["pending", "running"].includes(state.ocr.status);
  const runOcr = async () => { try { await api.post(`/projects/${caseId}/documents/${src.doc_id}/ocr`); toast.success("Leser dokumentet med OCR – teksten dukker opp her når den er klar"); setState((s) => ({ ...s, ocr: { status: "pending" } })); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-3" data-testid={`item-excerpt-${item.ref}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex h-7 items-center gap-1 rounded-[6px] px-1.5 text-[13px] font-medium text-foreground/60 transition-colors hover:bg-black/[0.04] hover:text-foreground" aria-expanded={open} data-testid={`item-excerpt-toggle-${item.ref}`}>
        {open ? "Skjul utdrag" : "Vis utdraget i dokumentet"} <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div className="mt-2 rounded-[12px] border border-black/[0.06] bg-[hsl(210_20%_98%)] px-4 py-3" data-testid={`item-excerpt-body-${item.ref}`}>
          {state.loading && !text ? <div className="flex items-center gap-2 text-[13px] text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Henter side {src.page} …</div>
            : state.err ? <p className="text-[13px] text-[hsl(var(--v-blocked))]">{state.err}</p>
              : !text ? (
                <div className="text-[13.5px] text-muted-foreground" data-testid={`item-excerpt-empty-${item.ref}`}>
                  {ocrBusy ? <span className="inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Siden er skannet – leses med OCR ({state.ocr.pages_done ?? 0}/{state.ocr.pages_total ?? "?"}) …</span>
                    : <span className="inline-flex flex-wrap items-center gap-2">Siden har ingen lesbar tekst. <button type="button" onClick={runOcr} className="inline-flex items-center gap-1 font-medium text-[hsl(var(--brand-strong))] hover:underline" data-testid={`item-excerpt-ocr-${item.ref}`}><ScanText className="h-3.5 w-3.5" /> Les med OCR</button></span>}
                </div>
              ) : (
                <>
                  <p className="text-[14px] leading-[1.65] text-foreground/80 [overflow-wrap:anywhere]" data-testid={`item-excerpt-text-${item.ref}`}>
                    {ex.partial && ex.before ? "… " : ""}{ex.before}<mark className="rounded-[2px] bg-[rgba(255,208,0,0.45)] px-0.5 text-foreground">{ex.hit}</mark>{ex.after}{ex.partial ? " …" : ""}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
                    <span>{src.doc_name ? src.doc_name.replace(/\.[a-z0-9]{2,5}$/i, "") : "Dokument"} · side {src.page}{state.page?.ocr ? " · lest med OCR" : ""}{!m ? " · fant ikke eksakt tekst, viser siden" : ""}</span>
                    {onOpen ? <button type="button" onClick={() => onOpen(src.doc_id, { page: src.page, pages: src.pages, q: queries, doc_name: src.doc_name })} className="font-medium text-[hsl(var(--brand-strong))] hover:underline" data-testid={`item-excerpt-open-${item.ref}`}>Åpne på siden</button> : null}
                  </div>
                </>
              )}
        </div>
      ) : null}
    </div>
  );
}
